import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'

interface UseInspectionRecorderOptions {
  autoRecord?: boolean
  /** 0 = 제한 없음. */
  recordTimeLimitSeconds?: number
}

/** 촬영 한 장 — 이름은 화면 '파일' 목록에 보이는 표기(옛 1.0·DS 요소와 같은 capture_YYYYMMDDHHmmss, 중복 허용). */
interface CaptureItem {
  name: string
  blob: Blob
}

const captureFrameAsBlob = (video: HTMLVideoElement): Promise<Blob | null> =>
  new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth || 1280
    canvas.height = video.videoHeight || 720
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      resolve(null)
      return
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg')
  })

/**
 * 검수 녹화·촬영 — `core.InspectionRecording` 은 시각 껍데기라 콜백이 없다(D11). 실제 스트림·녹화·캡처는
 * 이 훅이 `<video>` + MediaRecorder 로 직접 처리하고, 생성본의 녹화 슬롯(`wiring.recording`)이 같은 모양 위에
 * 미리보기(`attachVideo` 를 ref 로 단 `<video>`)·촬영 목록(`captureNames`)을 그린다.
 */
export const useInspectionRecorder = ({ autoRecord = false, recordTimeLimitSeconds = 0 }: UseInspectionRecorderOptions = {}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  /** 마지막으로 완성된 녹화본 — `stopAndWait` 가 녹화 중이 아닐 때 돌려준다(state 는 호출자 클로저에서 낡을 수 있다). */
  const lastBlobRef = useRef<Blob | null>(null)
  /** `stopAndWait` 호출자가 `onstop`(Blob 완성)까지 기다리는 대기열 — rgr11 `useMediaCapture` 의 stopWaiters 와 같은 방식. */
  const stopWaitersRef = useRef<((blob: Blob | null) => void)[]>([])
  const timeLimitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [isRecording, setIsRecording] = useState(false)
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null)
  const [captureItems, setCaptureItems] = useState<CaptureItem[]>([])
  const [isCameraReady, setIsCameraReady] = useState(false)
  /** 권한 거부·장치 없음·미지원 — 미리보기 칸 문구를 '연결 중' 과 가른다. */
  const [isCameraUnavailable, setIsCameraUnavailable] = useState(false)

  const captures = useMemo(() => captureItems.map((item) => item.blob), [captureItems])
  const captureNames = useMemo(() => captureItems.map((item) => item.name), [captureItems])

  /**
   * 미리보기 `<video>` 에 다는 ref — 붙는 순간 이미 받은 스트림을 잇는다.
   * 미리보기는 카메라가 준비된 뒤에야 그려지므로(스트림보다 늦게 붙는다) getUserMedia 시점의 대입만으로는 화면이 비었다.
   */
  const attachVideo = useCallback((video: HTMLVideoElement | null) => {
    videoRef.current = video
    const current = videoRef.current
    if (current && streamRef.current && current.srcObject !== streamRef.current) {
      current.srcObject = streamRef.current
    }
  }, [])

  const stop = useCallback(() => {
    if (timeLimitTimerRef.current) {
      clearTimeout(timeLimitTimerRef.current)
      timeLimitTimerRef.current = null
    }
    recorderRef.current?.stop()
  }, [])

  const start = useCallback(() => {
    const stream = streamRef.current
    // 녹화 중 판정은 레코더 상태로 — isRecording(state)은 onstop 직후 같은 틱에 다시 시작할 때 낡은 값이라 재시작을 막는다(자동녹화: 저장 뒤 다음 건).
    if (!stream || recorderRef.current?.state === 'recording') return

    chunksRef.current = []
    const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' })
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data)
    }
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' })
      lastBlobRef.current = blob
      setRecordingBlob(blob)
      setIsRecording(false)
      stopWaitersRef.current.splice(0).forEach((resolve) => resolve(blob))
    }
    recorder.start()
    recorderRef.current = recorder
    setIsRecording(true)

    if (recordTimeLimitSeconds > 0) {
      timeLimitTimerRef.current = setTimeout(stop, recordTimeLimitSeconds * 1000)
    }
  }, [recordTimeLimitSeconds, stop])

  /**
   * 녹화를 멈추고 녹화본이 완성될 때까지 기다린다(자동녹화 — 검수완료 저장 뒤 업로드할 녹화본). 녹화 중이 아니면 마지막 녹화본(없으면 null).
   * `stop()` 만 부르면 Blob 은 onstop 에서 비동기로 만들어져, 같은 흐름에서 읽으면 늘 비어 있다.
   * 돌려준 녹화본은 가져간 것으로 본다 — 남겨 두면 녹화 없이 저장한 다음 건이 이 녹화본을 다시 받는다(LOGISPRD-14589).
   */
  const stopAndWait = useCallback((): Promise<Blob | null> => {
    if (recorderRef.current?.state !== 'recording') {
      const blob = lastBlobRef.current
      lastBlobRef.current = null
      return Promise.resolve(blob)
    }
    return new Promise((resolve) => {
      stopWaitersRef.current.push((blob) => {
        lastBlobRef.current = null
        resolve(blob)
      })
      stop()
    })
  }, [stop])

  const capture = useCallback(async () => {
    if (!videoRef.current) return
    const blob = await captureFrameAsBlob(videoRef.current)
    if (blob) setCaptureItems((prev) => [...prev, { name: `capture_${dayjs().format('YYYYMMDDHHmmss')}`, blob }])
  }, [])

  /** '파일' 목록에서 한 장 지우기(태그 x) — 지운 장은 업로드에서도 빠진다. */
  const removeCapture = useCallback((index: number) => {
    setCaptureItems((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const reset = useCallback(() => {
    lastBlobRef.current = null
    setRecordingBlob(null)
    setCaptureItems([])
  }, [])

  /** 저장에 실어 보낸 촬영분만 지운다 — 저장 응답을 기다리는 사이 찍은 다음 건 촬영분은 남긴다(LOGISPRD-14589). 녹화본은 stopAndWait 가 이미 가져갔다. */
  const discard = useCallback((sent: Blob[]) => {
    setRecordingBlob(null)
    setCaptureItems((prev) => prev.filter((item) => !sent.includes(item.blob)))
  }, [])

  useEffect(() => {
    let cancelled = false

    if (!navigator.mediaDevices?.getUserMedia) {
      setIsCameraUnavailable(true)
      return undefined
    }

    // 영상만 요청한다 — audio 를 함께 요청하면 마이크가 없거나 거부된 PC 에서 카메라까지 실패한다.
    // 1.0(react-webcam audio 기본 false)과 같다 — 녹화본에 소리가 없는 것도 1.0 과 같다(LOGISPRD-14549).
    navigator.mediaDevices
      .getUserMedia({ video: true, audio: false })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }
        streamRef.current = stream
        if (videoRef.current) videoRef.current.srcObject = stream
        setIsCameraReady(true)
      })
      .catch(() => {
        setIsCameraReady(false)
        setIsCameraUnavailable(true)
      })

    return () => {
      cancelled = true
      // 스트림 트랙만 멈추면 녹화 중인 MediaRecorder 는 자동으로 멈추지 않아 onstop 이 안 불린다 —
      // 녹화 중 언마운트하면 setRecordingBlob 이 실행되지 않고 이후 업로드가 조용히 빠진다.
      if (recorderRef.current?.state === 'recording') {
        recorderRef.current.stop()
      }
      streamRef.current?.getTracks().forEach((track) => track.stop())
    }
  }, [])

  useEffect(() => {
    if (isCameraReady && autoRecord) start()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCameraReady, autoRecord])

  return {
    videoRef,
    attachVideo,
    isCameraReady,
    isCameraUnavailable,
    isRecording,
    start,
    stop,
    stopAndWait,
    recordingBlob,
    captures,
    captureNames,
    capture,
    removeCapture,
    reset,
    discard,
  }
}

export type UseInspectionRecorderResult = ReturnType<typeof useInspectionRecorder>
